import { useTranslation } from 'react-i18next';
import { Figure, FigureGroup } from '../../components/Figures';
import { formatBytes, formatMonth, formatNumber } from '../../lib/format';
import { TrendCard } from './TrendCard';
import type { TenantMetrics } from './useTenant';

const STATUSES = ['idea', 'draft', 'in_review', 'changes_requested', 'approved', 'archived'] as const;
const PLATFORMS = ['instagram', 'facebook'] as const;
const PUBLICATION_STATES = ['planned', 'scheduled', 'published', 'failed'] as const;

/** A trend and the media, knowledge, posts, publications and reels of one tenant: counts only (AI has its own tab). */
export function TenantUsageTab({ tenantId, metrics }: { readonly tenantId: string; readonly metrics: TenantMetrics }) {
  const { t } = useTranslation();
  const month = formatMonth(metrics.month);
  const { media, knowledge, content, reels } = metrics;
  return (
    <>
      <TrendCard tenantId={tenantId} />
      <FigureGroup title={t('tenant.media.title')}>
        <Figure label={t('tenant.media.photos')} value={formatNumber(media.photos)} />
        <Figure label={t('tenant.media.videos')} value={formatNumber(media.videos)} />
        <Figure label={t('tenant.media.audio')} value={formatNumber(media.audio)} />
        <Figure label={t('tenant.media.bytes')} value={formatBytes(media.bytes)} />
        <Figure label={t('tenant.media.tagged')} value={formatNumber(media.tagged)} />
      </FigureGroup>
      <FigureGroup title={t('tenant.knowledge.title')}>
        <Figure label={t('tenant.knowledge.items')} value={formatNumber(knowledge.items)} />
        <Figure label={t('tenant.knowledge.verified')} value={formatNumber(knowledge.verified)} />
      </FigureGroup>
      <FigureGroup title={t('tenant.content.title')}>
        {STATUSES.map((status) => (
          <Figure key={status} label={t(`tenant.content.${status}`)} value={formatNumber(content[status])} />
        ))}
        <Figure label={t('tenant.content.createdMonth', { month })} value={formatNumber(content.created_month)} />
        <Figure label={t('tenant.content.campaigns')} value={formatNumber(content.campaigns)} />
        <Figure label={t('tenant.content.activeCampaigns')} value={formatNumber(content.active_campaigns)} />
      </FigureGroup>
      {PLATFORMS.map((platform) => {
        const counts = metrics.publications[platform];
        return (
          <FigureGroup key={platform} title={t('tenant.publications.title', { platform: t(`tenant.platforms.${platform}`) })}>
            {PUBLICATION_STATES.map((state) => (
              <Figure key={state} label={t(`tenant.publications.${state}`)} value={formatNumber(counts[state])} />
            ))}
            <Figure label={t('tenant.publications.publishedMonth', { month })} value={formatNumber(counts.published_since)} />
          </FigureGroup>
        );
      })}
      <FigureGroup title={t('tenant.reels.title')}>
        <Figure label={t('tenant.reels.rendered')} value={formatNumber(reels.rendered)} />
        <Figure label={t('tenant.reels.failed')} value={formatNumber(reels.failed)} />
      </FigureGroup>
    </>
  );
}
